import React, { useState, useRef, useEffect } from 'react';
import { getCssVar } from '../contexts/ThemeContext';
import { trackMiniAppEvent, trackMiniAppTimeSpent } from '../utils/analytics';

export default function ColoringApp({ palette = ["#ff0000", "#00ff00", "#0000ff", "#ffff00", "#ff00ff", "#00ffff", "#ff8800", "#8800ff", "#008800", "#880000"], onAppFocus }) {
  const [selectedColor, setSelectedColor] = useState(palette[0]);
  const [brushSize, setBrushSize] = useState(5);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('brush'); // 'brush', 'eraser'
  const [startTime, setStartTime] = useState(null);
  const canvasRef = useRef(null);
  const contextRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = 600;
    canvas.height = 400;
    
    // Set initial canvas background
    context.fillStyle = getCssVar('--color-bg-card');
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = selectedColor;
    context.lineWidth = brushSize;
    
    contextRef.current = context;
    
    // Set start time when component mounts
    setStartTime(Date.now());
  }, []);

  // Track time spent when component unmounts
  useEffect(() => {
    return () => {
      if (startTime) {
        trackMiniAppTimeSpent('coloring', startTime);
      }
    };
  }, [startTime]);

  useEffect(() => {
    if (contextRef.current) {
      if (tool === 'eraser') {
        contextRef.current.strokeStyle = getCssVar('--color-bg-card');
      } else {
        contextRef.current.strokeStyle = selectedColor;
      }
      contextRef.current.lineWidth = brushSize;
    }
  }, [selectedColor, brushSize, tool]);

  const startDrawing = (e) => {
    // Track drawing started
    trackMiniAppEvent.coloring.drawingStarted();
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    contextRef.current.beginPath();
    contextRef.current.moveTo(x, y);
    setIsDrawing(true);
    if (onAppFocus) onAppFocus();
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    contextRef.current.lineTo(x, y);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    // Track drawing stopped
    trackMiniAppEvent.coloring.drawingStopped();
    
    contextRef.current.closePath();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    // Track canvas cleared
    trackMiniAppEvent.coloring.canvasCleared();
    
    // Track time spent before clearing
    if (startTime) {
      trackMiniAppTimeSpent('coloring', startTime);
      setStartTime(Date.now()); // Reset start time
    }
    
    const canvas = canvasRef.current;
    const context = contextRef.current;
    context.fillStyle = getCssVar('--color-bg-card');
    context.fillRect(0, 0, canvas.width, canvas.height);
  };

  const downloadCanvas = () => {
    // Track download clicked
    trackMiniAppEvent.coloring.downloadClicked();
    
    // Track time spent before download
    if (startTime) {
      trackMiniAppTimeSpent('coloring', startTime);
      setStartTime(Date.now()); // Reset start time
    }
    
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = 'coloring-app-drawing.png';
    link.href = canvas.toDataURL();
    link.click();
  };

  // Wrapper functions for settings changes with analytics
  const handleToolChange = (newTool) => {
    trackMiniAppEvent.coloring.toolChanged(newTool);
    setTool(newTool);
  };

  const handleBrushSizeChange = (newSize) => {
    trackMiniAppEvent.coloring.brushSizeChanged(newSize);
    setBrushSize(newSize);
  };

  const handleColorChange = (newColor) => {
    trackMiniAppEvent.coloring.colorSelected(newColor);
    setSelectedColor(newColor);
    setTool('brush');
  };

  return (
    <div style={{
      padding: '20px',
      background: 'var(--color-bg-card)',
      borderRadius: '12px',
      boxShadow: '0 4px 12px var(--color-shadow)',
      margin: '10px 0'
    }}>
      <h3 style={{ margin: '0 0 16px 0', color: 'var(--color-text)' }}>🎨 Drawing App</h3>
      
      {/* Tools */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '16px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => handleToolChange('brush')}
          style={{
            padding: '8px 12px',
            backgroundColor: tool === 'brush' ? 'var(--color-primary)' : 'var(--color-bg)',
            color: tool === 'brush' ? 'var(--color-bg-card)' : 'var(--color-text)',
            border: `2px solid var(--color-border)`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: tool === 'brush' ? 'bold' : 'normal'
          }}
        >
          🖌️ Brush
        </button>
        
        <button
          onClick={() => handleToolChange('eraser')}
          style={{
            padding: '8px 12px',
            backgroundColor: tool === 'eraser' ? 'var(--color-primary)' : 'var(--color-bg)',
            color: tool === 'eraser' ? 'var(--color-bg-card)' : 'var(--color-text)',
            border: `2px solid var(--color-border)`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: tool === 'eraser' ? 'bold' : 'normal'
          }}
        >
          🧽 Eraser
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: 'var(--color-text)', fontSize: '14px' }}>Size:</label>
          <input
            type="range"
            min="1"
            max="20"
            value={brushSize}
            onChange={(e) => handleBrushSizeChange(parseInt(e.target.value))}
            style={{
              width: '80px',
              accentColor: 'var(--color-primary)'
            }}
          />
          <span style={{ color: 'var(--color-text)', fontSize: '12px', minWidth: '20px' }}>
            {brushSize}
          </span>
        </div>
      </div>

      {/* Color Picker */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '16px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <label style={{ color: 'var(--color-text)', fontSize: '14px', fontWeight: 'bold' }}>
          Color:
        </label>
        <input
          type="color"
          value={selectedColor}
          onChange={(e) => handleColorChange(e.target.value)}
          style={{
            width: '50px',
            height: '40px',
            border: '2px solid var(--color-border)',
            borderRadius: '6px',
            cursor: 'pointer',
            backgroundColor: 'transparent'
          }}
          title="Choose any color"
        />
        <div style={{ 
          display: 'flex', 
          gap: '8px',
          alignItems: 'center'
        }}>
          <span style={{ color: 'var(--color-text)', fontSize: '12px' }}>
            {selectedColor}
          </span>
        </div>
      </div>

      {/* Common Colors */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        marginBottom: '16px',
        flexWrap: 'wrap'
      }}>
        {palette.map((color, index) => (
          <button
            key={index}
            onClick={() => handleColorChange(color)}
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: color,
              border: selectedColor === color ? `3px solid var(--color-text)` : `2px solid var(--color-border)`,
              borderRadius: '50%',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            title={`Select ${color}`}
          />
        ))}
      </div>

      {/* Canvas */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '16px'
      }}>
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          style={{
            border: `4px solid var(--color-text)`,
            borderRadius: '8px',
            cursor: 'crosshair',
            background: 'var(--color-bg-card)',
            boxShadow: '0 0 0 2px var(--color-border)'
          }}
        />
      </div>

      {/* Controls */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={clearCanvas}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-danger)',
            color: 'white',
            border: '2px solid var(--color-danger)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          🗑️ Clear Canvas
        </button>
        
        <button
          onClick={downloadCanvas}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-success)',
            color: 'var(--color-bg-card)',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Download
        </button>
      </div>
    </div>
  );
} 